import { Badge, Card } from '@dnx/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarPlus, ExternalLink, UserRound } from 'lucide-react';
import { useState } from 'react';
import { BookDialog } from '../agenda/book-dialog.js';
import { contactAppointmentsQuery, hhmm, KIND_LABEL, STATUS_STYLE, TZ } from '../agenda/queries.js';
import { Button } from '@dnx/ui';
import { PortalAccessBox } from '../contacts/portal-access.js';
import { StageControl } from '../crm/lead-fields.js';
import { leadByContactQuery } from '../crm/queries.js';
import { AREA_LABELS, initials, SOURCE_LABELS } from '../lib/labels.js';
import { meQuery } from '../lib/session.js';
import { conversationQuery, relTime } from './queries.js';
import { ContactTags } from '../reception/tags.js';

/** Funil no contexto da conversa: etapa, origem e atalho para o lead. */
function LeadBox({ contactId }: { contactId: string }) {
  const { data: me } = useSuspenseQuery(meQuery);
  const canRead = me.permissions.includes('crm.lead.read');
  const { data: lead, isLoading } = useQuery({ ...leadByContactQuery(contactId), enabled: canRead });
  if (!canRead || isLoading) return null;
  if (!lead) {
    return (
      <Card className="border-dashed p-3.5 text-[12.5px]">
        <p className="flex items-center gap-1.5 font-medium text-fg"><UserRound className="size-4 text-fg-subtle" />Sem lead no funil</p>
        <p className="mt-1 text-fg-muted">Esta pessoa não é uma oportunidade comercial ativa (por exemplo, paciente em acompanhamento).</p>
      </Card>
    );
  }
  return (
    <Card className="space-y-3 p-3.5 text-[12.5px]">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 font-medium text-fg"><UserRound className="size-4 text-fg-subtle" />Lead</p>
        <Link to="/leads" search={{ lead: lead.id }} className="flex items-center gap-1 text-primary hover:underline">Ver no funil<ExternalLink className="size-3" /></Link>
      </div>
      <div className="flex flex-wrap gap-1">
        <Badge tone={lead.source === 'meta_ads' ? 'primary' : 'neutral'}>{SOURCE_LABELS[lead.source]}</Badge>
        {lead.procedureArea && <Badge>{AREA_LABELS[lead.procedureArea]}</Badge>}
      </div>
      {lead.campaign && <p className="text-fg-muted">Anúncio: “{lead.campaign}”</p>}
      <StageControl lead={lead} canWrite={me.permissions.includes('crm.lead.write')} compact />
    </Card>
  );
}

/** Contexto ao lado da conversa (docs/04 §5.2). CRM e agenda entram nas próximas entregas. */
export function ContactPanel({ id, now }: { id: string; now: number }) {
  const { data: c } = useQuery(conversationQuery(id));
  if (!c) return null;
  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-col items-center pt-2 text-center">
        <span className="grid size-14 place-items-center rounded-full bg-surface-muted text-base font-semibold text-fg-muted ring-1 ring-border">{initials(c.contact.name)}</span>
        <p className="mt-2 font-semibold text-fg">{c.contact.name}</p>
        <p className="text-[12.5px] text-fg-muted">{[c.contact.instagram, c.contact.maskedPhone].filter(Boolean).join(' · ')}</p>
        <div className="mt-2.5 flex justify-center"><ContactTags contactId={c.contact.id} tags={c.tags} /></div>
      </div>
      <Card className="p-3.5 text-[12.5px]">
        <dl className="space-y-2">
          <div className="flex justify-between gap-2"><dt className="text-fg-muted">Canal</dt><dd className="text-right text-fg">{c.channel.displayName}</dd></div>
          <div className="flex justify-between gap-2"><dt className="text-fg-muted">Última mensagem</dt><dd className="text-fg">{relTime(c.lastMessageAt, now)}</dd></div>
        </dl>
      </Card>
      <LeadBox contactId={c.contact.id} />
      <AppointmentsBox contactId={c.contact.id} contactName={c.contact.name} />
      <PortalAccessBox contactId={c.contact.id} compact />
    </div>
  );
}

/** Próximas consultas do paciente + agendar direto da conversa. */
function AppointmentsBox({ contactId, contactName }: { contactId: string; contactName: string }) {
  const { data: me } = useSuspenseQuery(meQuery);
  const canRead = me.permissions.includes('scheduling.appointment.read');
  const canWrite = me.permissions.includes('scheduling.appointment.write');
  const { data: appts } = useQuery({ ...contactAppointmentsQuery(contactId), enabled: canRead });
  const [booking, setBooking] = useState(false);
  if (!canRead) return null;
  const day = (iso: string) => new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: TZ }).format(new Date(iso));
  return (
    <Card className="space-y-3 p-3.5 text-[12.5px]">
      <p className="flex items-center gap-1.5 font-medium text-fg"><CalendarPlus className="size-4 text-fg-subtle" />Agenda</p>
      {appts?.length ? (
        <ul className="space-y-2">
          {appts.map((a) => (
            <li key={a.id}>
              <Link to="/agenda" search={{ dia: new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date(a.startsAt)), consulta: a.id }}
                className={`block rounded-md border-l-4 px-2.5 py-2 ${STATUS_STYLE[a.status]!.box}`}>
                <span className="block font-medium">{KIND_LABEL[a.kind]} · {day(a.startsAt)} {hhmm(a.startsAt)}</span>
                <span className="block opacity-80">{a.professional?.name} · {STATUS_STYLE[a.status]!.label}</span>
                {a.confirmation === 'reschedule_requested' && <span className="block font-medium text-warning">Pediu para remarcar</span>}
              </Link>
            </li>
          ))}
        </ul>
      ) : <p className="text-fg-muted">Nenhuma consulta marcada.</p>}
      {canWrite && <Button size="sm" block variant="secondary" onClick={() => setBooking(true)}>Agendar avaliação</Button>}
      {booking && <BookDialog open onClose={() => setBooking(false)} contactId={contactId} contactName={contactName} />}
    </Card>
  );
}
